/**
 * Sección «Nombres de las prioridades»: renombra P0..P3 solo para mostrar.
 * La API, la base y los tokens siguen usando los códigos P0..P3.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  ETIQUETA_PRIORIDAD_POR_DEFECTO,
  LARGO_MAX_ETIQUETA_PRIORIDAD,
  PRIORIDADES,
  etiquetasPrioridadPorDefecto,
  etiquetasPrioridadSchema,
  type EtiquetasPrioridad,
  type Prioridad,
} from '@cdtec-backlog/contrato';
import { useAvisos } from '../../componentes/Avisos';
import { ErrorDeApi, mensajeDeError } from '../../lib/api';
import { useConfiguracion, useGuardarPrioridades } from './hooks';
import './SeccionPrioridades.css';

type Errores = Partial<Record<Prioridad, string>>;

/** Mismas reglas que la API; devuelve errores por campo y un error general (duplicados). */
export function validarEtiquetas(valores: EtiquetasPrioridad): {
  porCampo: Errores;
  general: string | null;
} {
  const porCampo: Errores = {};
  for (const p of PRIORIDADES) {
    const largo = valores[p].trim().length;
    if (largo === 0) porCampo[p] = 'No puede quedar vacío.';
    else if (largo > LARGO_MAX_ETIQUETA_PRIORIDAD)
      porCampo[p] = `Máximo ${LARGO_MAX_ETIQUETA_PRIORIDAD} caracteres.`;
  }
  if (Object.keys(porCampo).length > 0) return { porCampo, general: null };
  const resultado = etiquetasPrioridadSchema.safeParse(valores);
  return {
    porCampo,
    general: resultado.success ? null : 'Los nombres deben ser distintos entre sí.',
  };
}

export function SeccionPrioridades() {
  const configuracion = useConfiguracion();
  const guardar = useGuardarPrioridades();
  const { mostrar } = useAvisos();
  // Lo que la persona escribe manda; mientras no escriba, se ve lo guardado en el servidor.
  const [editado, setValores] = useState<EtiquetasPrioridad | null>(null);
  const valores = editado ?? configuracion.data?.prioridades ?? etiquetasPrioridadPorDefecto();
  const [errores, setErrores] = useState<Errores>({});
  const [general, setGeneral] = useState<string | null>(null);
  const refs = useRef<Partial<Record<Prioridad, HTMLInputElement | null>>>({});
  const refGeneral = useRef<HTMLParagraphElement | null>(null);

  // Lleva el foco al primer problema para que lo anuncie el lector de pantalla.
  useEffect(() => {
    const primero = PRIORIDADES.find((p) => errores[p]);
    if (primero) refs.current[primero]?.focus();
    else if (general) refGeneral.current?.focus();
  }, [errores, general]);

  const aplicarErrorDeApi = (error: unknown) => {
    if (error instanceof ErrorDeApi && error.estado === 400) {
      const nuevos: Errores = {};
      for (const d of error.detalles) {
        const campo = PRIORIDADES.find((p) => d.campo === p || d.campo.endsWith(`.${p}`));
        if (campo) nuevos[campo] = d.mensaje;
      }
      setErrores(nuevos);
      setGeneral(Object.keys(nuevos).length > 0 ? null : error.message);
      return;
    }
    mostrar(mensajeDeError(error), 'error');
  };

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    const entrada: EtiquetasPrioridad = {
      P0: valores.P0.trim(),
      P1: valores.P1.trim(),
      P2: valores.P2.trim(),
      P3: valores.P3.trim(),
    };
    const resultado = validarEtiquetas(entrada);
    setErrores(resultado.porCampo);
    setGeneral(resultado.general);
    if (Object.keys(resultado.porCampo).length > 0 || resultado.general) return;
    setErrores({});
    guardar.mutate(entrada, {
      onSuccess: (guardadas) => {
        setValores(guardadas);
        mostrar('Nombres guardados.');
      },
      onError: aplicarErrorDeApi,
    });
  };

  const restaurar = () => {
    setErrores({});
    setGeneral(null);
    guardar.mutate(etiquetasPrioridadPorDefecto(), {
      onSuccess: (guardadas) => {
        setValores(guardadas);
        mostrar('Nombres originales restaurados.');
      },
      onError: aplicarErrorDeApi,
    });
  };

  return (
    <section className="seccion-config" aria-labelledby="titulo-prioridades">
      <h2 id="titulo-prioridades">Nombres de las prioridades</h2>
      <p>
        Cambia cómo se ven las prioridades en el tablero. Solo cambia el nombre que se muestra: la
        API y los tokens siguen usando P0 a P3. Cada nombre debe ser distinto (hasta{' '}
        {LARGO_MAX_ETIQUETA_PRIORIDAD} caracteres).
      </p>
      {configuracion.isError && <p role="alert">{mensajeDeError(configuracion.error)}</p>}
      <form onSubmit={enviar} noValidate>
        {general && (
          <p role="alert" className="seccion-config__error" tabIndex={-1} ref={refGeneral}>
            {general}
          </p>
        )}
        {PRIORIDADES.map((p) => (
          <div className="campo" key={p}>
            <label htmlFor={`prioridad-${p}`}>
              Nombre de {p} <span className="seccion-config__pista">(código técnico)</span>
            </label>
            <input
              id={`prioridad-${p}`}
              type="text"
              ref={(el) => {
                refs.current[p] = el;
              }}
              value={valores[p]}
              placeholder={ETIQUETA_PRIORIDAD_POR_DEFECTO[p]}
              aria-invalid={errores[p] ? true : undefined}
              aria-describedby={errores[p] ? `error-prioridad-${p}` : undefined}
              onChange={(e) => setValores({ ...valores, [p]: e.target.value })}
            />
            {errores[p] && (
              <p id={`error-prioridad-${p}`} role="alert" className="seccion-config__error">
                {errores[p]}
              </p>
            )}
          </div>
        ))}
        <div className="seccion-config__acciones">
          <button type="submit" className="boton boton--primario" disabled={guardar.isPending}>
            {guardar.isPending ? 'Guardando…' : 'Guardar'}
          </button>
          <button type="button" className="boton" onClick={restaurar} disabled={guardar.isPending}>
            Restaurar nombres originales
          </button>
        </div>
      </form>
    </section>
  );
}
